import {useMemo} from 'react';
import * as Yup from 'yup';
import {AuthCodeDTO, AuthPasswordDTO} from '../../../entities';

export type ValidationSchemes = {
  passwordSchema: Yup.ObjectSchema<AuthPasswordDTO>;
  codeSchema: Yup.ObjectSchema<AuthCodeDTO>;
  identifierSchema: Yup.ObjectSchema<{identifier: string}>;
  emailSchema: Yup.StringSchema;
  emailNotRequiredSchema: Yup.StringSchema<string | null | undefined>;
  phoneNumberNotRequiredSchema: Yup.StringSchema<string | null | undefined>;
  displayNameSchema: Yup.StringSchema;
  displayNameNotRequiredSchema: Yup.StringSchema;
  realNameNotRequiredSchema: Yup.StringSchema;
  phoneNumberSchema: Yup.StringSchema;
  regionNameSchema: Yup.StringSchema;
  nameSchema: Yup.StringSchema;
  orderSchema: Yup.NumberSchema;
  fundNameSchema: Yup.StringSchema;
  shortNameSchema: Yup.StringSchema;
  shortDescriptionSchema: Yup.StringSchema;
  detailedDescriptionSchema: Yup.StringSchema;
  websiteSchema: Yup.StringSchema<string | null | undefined>;
};

export const useSchemes = (): ValidationSchemes => {
  const passwordSchema = useMemo(
    () =>
      Yup.object({
        password: Yup.string().required('Пароль обязателен').min(6, 'Пароль должен содержать минимум 6 символов'),
      }),
    [],
  );

  const codeSchema = useMemo(
    () =>
      Yup.object({
        code: Yup.string()
          .required('Код подтверждения обязателен')
          .matches(/^\d+$/, 'Код должен содержать только цифры')
          .length(6, 'Код должен содержать 6 цифр'),
      }),
    [],
  );
  const identifierSchema = useMemo(
    () =>
      Yup.object({
        identifier: Yup.string().required('Имя пользователя или почта обязательны'),
      }),
    [],
  );

  const emailSchema = useMemo(() => Yup.string().email('Некорректный email').required('Email обязателен'), []);

  const phoneNumberSchema = Yup.string()
    .required('Номер телефона обязателен')
    .matches(/^\+?[0-9]{10,15}$/, 'Некорректный формат номера телефона');

  const displayNameSchema = useMemo(() => Yup.string().required('Имя обязательно'), []);
  const displayNameNotRequiredSchema = useMemo(() => Yup.string(), []);
  const realNameNotRequiredSchema = useMemo(() => Yup.string(), []);
  const regionNameSchema = useMemo(() => Yup.string().required('Название региона обязательно'), []);
  const nameSchema = useMemo(() => Yup.string().required('Название обязательно'), []);
  const orderSchema = useMemo(() => Yup.number().required('Порядок обязателен'), []);

  const fundNameSchema = useMemo(() => Yup.string().required('Название фонда обязательно'), []);
  const shortNameSchema = useMemo(() => Yup.string(), []);

  const shortDescriptionSchema = useMemo(
    () =>
      Yup.string()
        .required('Краткое описание обязательно')
        .max(500, 'Краткое описание не должно превышать 500 символов'),
    [],
  );

  const detailedDescriptionSchema = useMemo(() => Yup.string().required('Подробное описание обязательно'), []);

  const emailNotRequiredSchema = useMemo(() => Yup.string().email('Некорректный email').notRequired(), []);
  const phoneNumberNotRequiredSchema = useMemo(
    () =>
      Yup.string()
        .matches(/^\+?[0-9]{10,15}$/, 'Некорректный формат номера телефона')
        .notRequired(),
    [],
  );

  const websiteSchema = useMemo(
    () =>
      Yup.string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .matches(
          /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)$/,
          'Некорректный формат URL',
        ),
    [],
  );

  return {
    passwordSchema,
    codeSchema,
    identifierSchema,
    emailSchema,
    displayNameSchema,
    displayNameNotRequiredSchema,
    realNameNotRequiredSchema,
    phoneNumberSchema,
    regionNameSchema,
    nameSchema,
    orderSchema,
    fundNameSchema,
    shortNameSchema,
    shortDescriptionSchema,
    detailedDescriptionSchema,
    websiteSchema,
    emailNotRequiredSchema,
    phoneNumberNotRequiredSchema,
  };
};
